import { ThemeProvider } from "@emotion/react";
import {
  Box,
  BoxProps,
  createTheme,
  InputAdornment,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { STheme } from "components/styles";
import { SDialog, SDialogProps, SInputProps } from "components/parts";
import { useRecoilValue } from "recoil";
import { focusState } from "states";
import { RefCallback, useState } from "react";
import { WebAssetSharp } from "@mui/icons-material";
import { SCancelButton } from "components";
import { labels } from "messages/labels";

/**
 * 入力フィールド
 */

// TODO: 文字色設定を追加する

/**
 * プロパティ
 */
export type SFieldProps = Partial<Omit<TextFieldProps, "size">> &
  SInputProps &
  Partial<SDialogProps> &
  Pick<BoxProps, "gridColumn" | "gridRow">;

/**
 * 入力フィールド
 *
 * @param props
 * @returns JSX.Element
 */
export const SField = ({
  focusId,
  index,
  align,
  formRef,
  gridColumn,
  gridRow,
  dialog,
  handleSubmit: submit,
  onClick,
  ...props
}: SFieldProps): JSX.Element => {
  // フォーカス設定
  const { focusRef, move } = useRecoilValue(focusState(focusId ?? ""));

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);
  const handleSubmit = (data: any) => {
    submit?.call(this, data);
    setOpen(false);
  };

  const action = (
    <SCancelButton label={labels.buttons.close} onClick={handleClose} />
  );

  const hasDialog =
    typeof dialog !== "undefined" &&
    typeof submit !== "undefined" &&
    !props.disabled;

  // テーマ設定
  const theme = createTheme(
    {
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 0,
              paddingRight: "3px",
              backgroundColor: STheme.fields.normal,
              borderColor: STheme.fields.border,
              "&.Mui-disabled": {
                color: STheme.fields.text,
                backgroundColor: STheme.fields.disabled,
              },
            },
            input: {
              textAlign: align,
              fontSize: "1rem",
              paddingLeft: "4px",
              paddingRight: "0px",
              paddingTop: "1px",
              paddingBottom: "0px",
              color: STheme.fields.text,
              "&.Mui-disabled": {
                fontSize: "0.9rem",
                WebkitTextFillColor: STheme.fields.text,
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              margin: 0,
            },
          },
        },
      },
    },
    { ...STheme }
  );

  return (
    <ThemeProvider theme={theme}>
      <Box gridColumn={gridColumn} gridRow={gridRow}>
        <TextField
          {...props}
          inputRef={(element) => {
            (props.inputRef as RefCallback<any>)?.call(this, element);
            formRef(element);
            focusRef(index, element);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
            props.onKeyDown?.call(this, event);
            move(index, event);
          }}
          inputProps={{ ...props.inputProps, tabIndex: index ?? -1 }}
          InputProps={
            hasDialog
              ? {
                  ...props.InputProps,
                  endAdornment: (
                    <Box tabIndex={0}>
                      <InputAdornment
                        position="end"
                        tabIndex={0}
                        onClick={(event) => {
                          onClick?.call(this, event);
                          handleOpen();
                        }}
                      >
                        <WebAssetSharp fontSize="medium" />
                      </InputAdornment>
                    </Box>
                  ),
                }
              : props.InputProps
          }
          autoComplete="off"
          fullWidth
        />
      </Box>
      {hasDialog && (
        <SDialog
          open={open}
          dialog={dialog}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          action={action}
        />
      )}
    </ThemeProvider>
  );
};
